/**
 * Feature flags — kill-switch per funzionalità fuori percorso.
 *
 * Tesi di prodotto: l'app è UN percorso (carica → ripassa → orale → report →
 * progressi), non una cassetta degli attrezzi. Ogni voce in più nel menu è un
 * bivio che allontana dal percorso. Queste feature restano nel codice — si
 * spengono gli INGRESSI, non si demolisce niente.
 *
 * Gated da env. Flag assente = feature SPENTA (default sicuro: chi non sa del
 * flag non riaccende per sbaglio). Riaccendere costa una env var + rebuild,
 * mai un revert di codice.
 *
 * --- REFERRAL (promozione del proprio codice) -----------------------------
 * Dati gen→lug 2026 (7 mesi): 1,0% dei free e 3,7% dei paganti, 53 utenti
 * totali, mai sopra il 2% mensile. Debole in ENTRAMBI i segmenti → non è un
 * problema di targeting, è un problema di domanda.
 *
 * ⚠️ Questo flag nasconde SOLO la PROMOZIONE (pagina "Invita e guadagna",
 * voci di menu, box, card post-pagamento). NON tocca il RISCATTO: chi è stato
 * invitato inserisce il codice nel campo promo di Stripe Checkout
 * (`allow_promotion_codes: true` in `lib/stripeFn.js`), percorso server-side
 * che non passa da nessuno di questi ingressi. L'acquisizione resta intatta.
 *
 * --- GLOSSARIO: NON spento (deciso il 27/07) ------------------------------
 * Era in questa PR, ne è stato tolto per due ragioni emerse dopo:
 * 1. Ricontando i paganti VERI (da `students.subscriptionStartedAt`, non
 *    dall'etichetta `plan` che era ferma al 13/01 e includeva i trial), il
 *    glossario risulta allo 0,2% dei free e al 4,5% dei paganti: skew 20×,
 *    il secondo più alto dell'app dopo l'interrogazione. Non è marginale
 *    ovunque — è marginale fra i free e usato quasi solo da chi paga.
 * 2. È uno **strumento compensativo** (ausilio DSA), già riservato ai Premium.
 *    Spegnerlo non è semplificare un menu: è togliere un'accessibilità.
 *
 * --- CONSEGUENZA ANALYTICS ------------------------------------------------
 * Da questa data gli eventi `referral_*` vanno a ZERO per costruzione. Lo zero
 * misura questa decisione, NON il comportamento degli utenti. Annotato in
 * `Product/Analytics/EVENTI-PRODOTTO.md`.
 *
 * Riaccensione (preproduction prima, poi production):
 *   REACT_APP_FEATURE_REFERRAL=true
 * Il flag acceso ripristina il comportamento identico a prima dello spegnimento.
 */

export const FEATURE_REFERRAL_ENABLED = process.env.REACT_APP_FEATURE_REFERRAL === 'true';

/** Promozione referral (pagina, menu, banner, box). NON il riscatto codice. */
export const isReferralEnabled = () => FEATURE_REFERRAL_ENABLED;

/*
 * --- MATURITÀ: ingresso stagionale (spento 13/08/2026) ---------------------
 * Dati Firestore (exam.isMaturita==true, misurati il 13/08/2026): 46 sessioni
 * TOTALI e 20 utenti distinti da sempre; picchi a marzo (27 sessioni / 8
 * utenti, in parte test interni) e giugno (15 / 10, la finestra d'esame vera);
 * ULTIMA sessione il 28/06 — luglio e agosto a ZERO. La finestra 2026 è
 * chiusa: si spengono gli INGRESSI (voce nav desktop/mobile + banner), il
 * codice resta. La rotta /home/maturita-esame continua a funzionare.
 *
 * CONSEGUENZA ANALYTICS: da questa data gli eventi maturita_entry_* e
 * maturita_banner_* vanno a zero per costruzione. Lo zero misura questa
 * decisione, NON il comportamento degli utenti.
 *
 * Riaccensione (verso la primavera 2027, preproduction prima):
 *   REACT_APP_FEATURE_MATURITA=true
 */
export const FEATURE_MATURITA_ENABLED = process.env.REACT_APP_FEATURE_MATURITA === 'true';

/** Ingressi Maturità (voce nav, banner, badge readiness). NON la rotta. */
export const isMaturitaEntryEnabled = () => FEATURE_MATURITA_ENABLED;

/**
 * Slot "App per iPhone" nella landing nuova (D5 del piano landing).
 * Nasce SPENTO: l'app non e' su nessuno store (push ASC fermo sull'account
 * demo). Il giorno dell'approvazione Apple si accende con la env var, senza
 * riaprire il codice:
 *   REACT_APP_FEATURE_APP_SLOT=true
 */
export const FEATURE_APP_SLOT_ENABLED = process.env.REACT_APP_FEATURE_APP_SLOT === 'true';

/**
 * URL di download dell'app iPhone (App Store o link pubblico TestFlight).
 * La sezione "App per iPhone" della landing nuova esiste sempre; il BOTTONE
 * di download compare solo se questo URL e' impostato: mai "scarica l'app"
 * prima che sia vero. ASC App ID 6759411842 → quando Apple approva:
 *   REACT_APP_IOS_STORE_URL=https://apps.apple.com/it/app/id6759411842
 */
export const IOS_STORE_URL = (process.env.REACT_APP_IOS_STORE_URL || '').trim();

/**
 * --- CODA DOCUMENTI SUL WEB (202 come l'app) -------------------------------
 * Oggi il web tiene aperta /process-document finche' il motore ha finito:
 * 149 s misurati per 3.000.000 di caratteri, dentro un budget di 600 s. Se
 * la scheda si chiude, la rete cade o il budget scade, il server continua
 * a generare (e a costare) mentre lo studente vede un errore. L'app mobile
 * non ha questo problema: chiama /process-document-streaming, riceve 202 e
 * legge l'esito dal listener su `processingStatus` (STU-154).
 *
 * Con questo flag il web fa lo stesso. Nasce SPENTO: si costruisce, non si
 * accende. Il percorso sincrono resta intatto come default e come ripiego.
 * Prima di accenderlo in produzione: preproduction, e la lista di cio' che il
 * worker NON scrive sul documento rispetto al percorso sincrono (vedi il
 * report del 2/09/2026 sul flusso di caricamento).
 *   REACT_APP_WEB_QUEUE_ENABLED=true
 */
export const WEB_QUEUE_ENABLED = process.env.REACT_APP_WEB_QUEUE_ENABLED === 'true';

/** Il web accoda su Cloud Tasks (202) invece di tenere aperta la richiesta. */
export const isWebQueueEnabled = () => WEB_QUEUE_ENABLED;

/**
 * --- RISPOSTA BREVE: il popup CHIEDE invece di decidere (STU-292) ----------
 * Oggi il registratore d'esame ha un solo controllo sulla risposta parlata:
 * stringa vuota. "Sì." (una parola) passa, viene salvata, prende 0, e quello 0
 * entra nella media del voto. Con questo flag, sotto le cinque parole compare
 * un popup con TRE strade — riprovo / è completa / salto la domanda — invece di
 * decidere al posto dello studente.
 *
 * Nasce SPENTO. Il popup `EmptyResponsePopup` e' condiviso con la Ripetizione:
 * il raggio d'azione e' entrambe le modalita', quindi si accende prima su
 * preproduction e si guarda cosa succede alle risposte da una parola.
 * Con il flag spento il comportamento e' identico a prima: solo la risposta
 * vuota apre il popup, e quello a un bottone solo.
 *   REACT_APP_SHORT_ANSWER_CONFIRM=true
 */
export const SHORT_ANSWER_CONFIRM_ENABLED =
  process.env.REACT_APP_SHORT_ANSWER_CONFIRM === 'true';

/** Sotto le 5 parole il registratore chiede conferma invece di salvare e basta. */
export const isShortAnswerConfirmEnabled = () => SHORT_ANSWER_CONFIRM_ENABLED;
